import {
  HStack,
  IconButton,
  IconButtonWithTooltip,
  Menu,
  MenuButton,
  MenuItem,
  MenuList,
  Text,
  Tooltip,
  useDisclosure,
} from '@elkaso-app/web-design';
import { useAbility } from '@casl/react';
import { AbilityContext } from 'configs/can';
import { usePageParams } from 'hooks/use-page-params';
import Icon from '@mdi/react';
import {
  mdiBorderColor,
  mdiHomeAssistant,
  mdiTruck,
  mdiShieldStar,
  mdiSourceMerge,
  mdiCart,
  mdiWarehouse,
  mdiCloudUpload,
  mdiAccount,
  mdiFileDocumentOutline,
  mdiDotsHorizontal,
  mdiAccountTie,
} from '@mdi/js';
import { useMemo } from 'react';
import { useHistory } from 'react-router-dom';
import { BulkUploadModal } from './bulk-upload-modal';
import type { CmsRestaurantRow } from 'apis/restaurants/types';
import { AssignAccountManagerModal } from './assign-account-manager-modal';

export interface IRestaurantActionsCell {
  restaurant: CmsRestaurantRow;
}

interface ActionItem {
  key: string;
  label: string;
  icon: string;
  onClick: () => void;
  /** Hidden unless the user can update User (legacy gate for Edit / Merge) */
  requiresUpdate?: boolean;
  /** Hidden unless the user can update Restaurant (the subject the UMS endpoint checks) */
  requiresRestaurantUpdate?: boolean;
}

export const RestaurantActionsCell = ({ restaurant }: IRestaurantActionsCell) => {
  const history = useHistory();
  const ability = useAbility(AbilityContext);
  const { isOpen, onOpen, onClose } = useDisclosure();
  const accountManagerModal = useDisclosure();
  const { getPageParams, setPageParams } = usePageParams();

  const id = restaurant.id;
  const countryCode = restaurant.countryCode;

  const onBulkUpload = () => {
    const params = getPageParams();
    setPageParams({ ...params, restaurantId: id });
    onOpen();
  };

  // Same order as legacy restaurantsList.js actions array
  const allActions: ActionItem[] = useMemo(
    () => [
      {
        key: 'edit',
        label: 'Edit',
        icon: mdiBorderColor,
        onClick: () => history.push(`/app/restaurants/edit/${id}`),
        requiresUpdate: true,
      },
      {
        key: 'branches',
        label: 'Branches',
        icon: mdiHomeAssistant,
        onClick: () => history.push(`/app/restaurants/branch/list/${id}`),
      },
      {
        key: 'suppliers',
        label: 'Suppliers',
        icon: mdiTruck,
        onClick: () => history.push(`/app/restaurants/supplier/${id}/${countryCode}`),
      },
      {
        key: 'kpi',
        label: 'KPI',
        icon: mdiShieldStar,
        onClick: () => history.push(`/app/restaurants/kpi/${id}`),
      },
      {
        key: 'merge',
        label: 'Merge',
        icon: mdiSourceMerge,
        onClick: () => history.push(`/app/restaurants/merge/${id}`),
        requiresUpdate: true,
      },
      {
        key: 'orders',
        label: 'Orders',
        icon: mdiCart,
        onClick: () => history.push(`/app/restaurants/orders-list/${id}`),
      },
      {
        key: 'warehouses',
        label: 'Warehouses',
        icon: mdiWarehouse,
        onClick: () => history.push(`/app/restaurants/warehouse/list/${id}`),
      },
      {
        key: 'bulk-upload',
        label: 'Bulk Upload',
        icon: mdiCloudUpload,
        onClick: onBulkUpload,
      },
      {
        key: 'users',
        label: 'Restaurant Users',
        icon: mdiAccount,
        onClick: () => history.push(`/app/restaurants/restaurant-users/${id}`),
      },
      {
        key: 'documents',
        label: 'Document Vault',
        icon: mdiFileDocumentOutline,
        onClick: () => history.push(`/app/restaurants/document-vault/${id}`),
      },
      {
        key: 'account-manager',
        label: 'Account Manager',
        icon: mdiAccountTie,
        onClick: accountManagerModal.onOpen,
        requiresRestaurantUpdate: true,
      },
    ],
    [id, countryCode, accountManagerModal.onOpen]
  );

  // Edit and Merge keep the legacy `update User` gate; the account-manager action follows the
  // `update Restaurant` ability the UMS endpoint enforces.
  const actions = useMemo(() => {
    const canUpdateUser = ability.can('update', 'User');
    const canUpdateRestaurant = ability.can('update', 'Restaurant');
    return allActions.filter(
      (action) => (!action.requiresUpdate || canUpdateUser) && (!action.requiresRestaurantUpdate || canUpdateRestaurant)
    );
  }, [allActions, ability]);

  const iconProps = { size: 0.75, color: '#757575' };

  // Legacy ViewMoreActions pattern: first 2 inline, rest in 3-dot dropdown
  const inlineActions = actions.slice(0, 2);
  const overflowActions = actions.slice(2);

  return (
    <>
      <HStack spacing='0'>
        {inlineActions.map((action) => (
          <IconButtonWithTooltip
            key={action.key}
            Icon={<Icon path={action.icon} {...iconProps} />}
            label={action.label}
            aria-label={action.key}
            variant='ghost'
            size='sm'
            onClick={action.onClick}
          />
        ))}

        {overflowActions.length > 0 && (
          <Menu placement='bottom-end'>
            <Tooltip label='More actions' placement='top'>
              <MenuButton as={IconButton} icon={<Icon path={mdiDotsHorizontal} {...iconProps} />} aria-label='more-actions' variant='ghost' size='sm' />
            </Tooltip>

            <MenuList>
              {overflowActions.map((action) => (
                <MenuItem key={action.key} onClick={action.onClick} icon={<Icon path={action.icon} {...iconProps} />}>
                  <Text>{action.label}</Text>
                </MenuItem>
              ))}
            </MenuList>
          </Menu>
        )}
      </HStack>

      <BulkUploadModal isOpen={isOpen} onClose={onClose} />
      <AssignAccountManagerModal
        isOpen={accountManagerModal.isOpen}
        onClose={accountManagerModal.onClose}
        restaurant={restaurant}
      />
    </>
  );
};
